import { Box, Collapse, HStack, useDisclosure } from "@chakra-ui/react";
import { LuCalendarPlus } from "react-icons/lu";
import CDS from "design-system";
import CountBadge from "components/CountBadge";
import { Meeting } from "features/Resources/Meeting/models";
import { PrefixId } from "features/Resources/Shared/models";
import { meetingReadiness } from "features/Resources/Meeting/helpers";
import ScheduleActionRow, { SoftButton } from "./ScheduleActionRow";

interface NeedsActionCardProps {
  toFix: Meeting[];
  toSchedule: Meeting[];
  onExpand: (prefixId: PrefixId) => void;
  onScheduleAll: () => void;
  startDate?: Date | string;
}

function SubsectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <CDS.Text12 fontWeight="bold" color="fg.subtle" letterSpacing="0.06em">
      {children}
    </CDS.Text12>
  );
}

/** Consolidated action queue above the schedule: things to fix, then things to schedule. */
export default function NeedsActionCard({ toFix, toSchedule, onExpand, onScheduleAll, startDate }: NeedsActionCardProps) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const total = toFix.length + toSchedule.length;

  if (total === 0) return null;

  return (
    <CDS.Card padding="none" radius="large" elevation="small">
      <HStack justify="space-between" align="center" px={4} py={3}>
        <HStack spacing={2} align="center">
          <CDS.Text16 fontWeight="semibold">Needs action</CDS.Text16>
          <CountBadge count={total} bg="orange.800" />
        </HStack>
        <Box as="button" type="button" onClick={onToggle}>
          <CDS.Text12 color="fg.muted">{isOpen ? "Hide" : "Show"}</CDS.Text12>
        </Box>
      </HStack>

      <Collapse in={isOpen} animateOpacity unmountOnExit>
        {toFix.length > 0 && (
          <Box px={4} pb={3} borderTop="1px solid" borderColor="border.subtle" pt={3}>
            <SubsectionLabel>TO FIX · {toFix.length}</SubsectionLabel>
            {toFix.map((meeting) => (
              <ScheduleActionRow
                key={meeting.id}
                meeting={meeting}
                variant="fix"
                reason={meetingReadiness(meeting).label}
                onExpand={onExpand}
              />
            ))}
          </Box>
        )}

        {toSchedule.length > 0 && (
          <Box px={4} pb={3} borderTop="1px solid" borderColor="border.subtle" pt={3}>
            <HStack justify="space-between" align="center">
              <SubsectionLabel>TO SCHEDULE · {toSchedule.length}</SubsectionLabel>
              <SoftButton
                label={`Schedule all ${toSchedule.length}`}
                icon={LuCalendarPlus}
                tone="blue"
                onClick={onScheduleAll}
              />
            </HStack>
            {toSchedule.map((meeting) => (
              <ScheduleActionRow
                key={meeting.id}
                meeting={meeting}
                variant="schedule"
                startDate={startDate}
                onExpand={onExpand}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </CDS.Card>
  );
}
